import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import {
  Avatar,
  Badge,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Switch,
  Tooltip,
} from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { styled, useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import type { FC, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { TicketTabs } from 'components/Headers/TicketTabs';
import type { MenuOptionI } from 'components/Menu/BaseMenu';
import BaseMenu from 'components/Menu/BaseMenu';
import { useAuth } from 'contexts/AuthContext';
import { ThemeModeE, useThemeMode } from 'contexts/MUIProvider';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import { RoutePathsE } from 'declarations/enums/routes';
import { UserTypesE } from 'declarations/enums/users';
import { AVATAR_STYLES, getConsistentAvatarBackgroundColor } from 'helpers/avatar';
import { getInitials } from 'helpers/string';
import useNotificationsHistory from 'pages/TicketsPage/components/NotificationsHistory/hooks/useNotificationsHistory';

export type NavTabItemT = {
  label: string;
  isActive: boolean;
  count?: number;
  hasCount?: boolean;
  onClick: () => void;
};

interface MainHeaderProps {
  navTabs?: NavTabItemT[];
  toolbarActions?: ReactNode;
  openedTicketTabs?: Record<string, string>;
  ticketTabIndex?: number;
  onTicketTabClick?: (ticketId: string, route: string) => void;
  onCloseTicketTab?: (e: React.MouseEvent, tabLabel: string, ticketId: string) => void;
}

const getTabCountSuffix = (tab: NavTabItemT, hideCount = false): string => {
  if (hideCount) return '';
  if (tab.count !== undefined) return ` (${tab.count})`;
  if (tab.hasCount) return ' (...)';
  return '';
};

const NavButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'active',
})<{ active: boolean }>(({ active, theme }) => ({
  fontSize: '12px',
  fontWeight: 500,
  color: active ? 'rgba(0, 0, 0, 0.87)' : 'white',
  backgroundColor: active ? '#E0E0E0' : 'transparent',
  padding: '4px 8px',
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
    padding: '2px 6px',
  },
  '&:hover': {
    backgroundColor: active ? 'white' : 'rgba(255, 255, 255, 0.08)',
    color: active ? 'rgba(0, 0, 0, 0.95)' : 'white',
  },
}));

export const MainHeader: FC<MainHeaderProps> = ({
  navTabs = [],
  toolbarActions,
  openedTicketTabs,
  ticketTabIndex = 0,
  onTicketTabClick,
  onCloseTicketTab,
}): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { mode, toggleMode } = useThemeMode();
  const { signOut } = useAuth();
  const { userProfile, isSuccessfullyFetchedUserProfile } = useUserProfile();
  const { isMobile } = useResponsive();
  const isDarkMode = theme.palette.mode === 'dark';

  const [notifAnchorEl, setNotifAnchorEl] = useState<HTMLButtonElement | null>(null);
  const notifOpen = Boolean(notifAnchorEl);

  const {
    notificationsHistory,
    isLoadingNotificationsHistory,
    hasUnreadNotifications,
    unreadNotificationsAmount,
    showUnreadNotifications,
    handleNotificationContentClick,
    markNotificationAsRead,
    markNotificationsAsRead,
    toggleUnreadFilter,
  } = useNotificationsHistory();

  const handleLogOut = useCallback(() => {
    signOut();
  }, [signOut]);

  const handleHome = useCallback(() => {
    navigate(RoutePathsE.TICKETS_PAGE);
  }, [navigate]);

  const handleProfile = useCallback(() => {
    navigate(RoutePathsE.PROFILE_PAGE);
  }, [navigate]);

  const isOnProfilePage = location.pathname === RoutePathsE.PROFILE_PAGE;

  const menuItems: MenuOptionI[] = useMemo<MenuOptionI[]>(
    () => [
      ...(isOnProfilePage
        ? [{ optionText: 'Home', callback: handleHome }]
        : [{ optionText: 'Profile', callback: handleProfile }]),
      { optionText: 'Sign Out', callback: handleLogOut },
    ],
    [isOnProfilePage, handleHome, handleProfile, handleLogOut]
  );

  const hasOneOfNames = userProfile?.firstname || userProfile?.lastname;

  const userName = hasOneOfNames
    ? `${userProfile?.firstname ?? ''} ${userProfile?.lastname ?? ''}`.trim()
    : userProfile?.displayName ?? '';

  return (
    <Box>
      <AppBar
        sx={{
          top: 0,
          position: 'fixed',
          boxShadow: 'none',
          borderBottom: (t): string => `1px solid ${t.palette.divider}`,
          backgroundColor:
            theme.palette.background?.paperElevation1 ||
            theme.palette.background?.default ||
            theme.palette.background?.paper ||
            '#FAFAFA',
        }}
      >
        <Box sx={{ bgcolor: theme.palette.header?.blue || '#182955', px: 4 }}>
          <Toolbar disableGutters>
            <Box sx={{ width: isMobile ? 'auto' : '50%', display: 'flex', alignItems: 'center' }}>
              {!isMobile && (
                <Typography
                  variant="h6"
                  noWrap
                  component="a"
                  onClick={(): void => navigate('/')}
                  sx={{
                    mr: 2,
                    fontWeight: 700,
                    color: 'inherit',
                    textDecoration: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Chirp
                </Typography>
              )}
              {navTabs.length > 0 && (
                <Box
                  sx={{
                    ml: isMobile ? 0 : 3,
                    display: 'flex',
                    gap: isMobile ? 0.5 : 2,
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                  }}
                >
                  {navTabs.map(tab => (
                    <NavButton key={tab.label} active={tab.isActive} onClick={tab.onClick}>
                      {tab.label}
                      {getTabCountSuffix(tab, isMobile)}
                    </NavButton>
                  ))}
                </Box>
              )}
            </Box>
            <Box sx={{ width: isMobile ? 'auto' : '50%' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {!isMobile && isSuccessfullyFetchedUserProfile && userName && (
                  <Typography variant="h5" sx={{ mr: 1 }}>
                    Welcome, {userName}.
                  </Typography>
                )}

                {!isMobile && (
                  <Tooltip
                    title={isSuccessfullyFetchedUserProfile ? `${userName} (Me)` : ''}
                    arrow
                    placement="bottom"
                  >
                    <Avatar
                      src={userProfile?.photoUrl}
                      sx={{
                        backgroundColor: getConsistentAvatarBackgroundColor(
                          userProfile?.id,
                          isDarkMode
                        ),
                        ...AVATAR_STYLES.size,
                        fontSize: AVATAR_STYLES.fontSize,
                      }}
                    >
                      {isSuccessfullyFetchedUserProfile ? getInitials(userName || '') : ''}
                    </Avatar>
                  </Tooltip>
                )}
                {!isMobile && (
                  <Box id="pendo-badge-anchor" sx={{ width: 30, height: 30, ml: 1, mr: 2 }} />
                )}
                <IconButton
                  aria-label="Toggle theme"
                  onClick={toggleMode}
                  sx={{ color: t => t.palette.common.white }}
                >
                  {mode === ThemeModeE.Dark ? (
                    <LightModeIcon sx={{ width: 24, height: 24 }} />
                  ) : (
                    <DarkModeIcon sx={{ width: 24, height: 24 }} />
                  )}
                </IconButton>
                <IconButton
                  aria-label="Notifications"
                  onClick={(e): void => setNotifAnchorEl(e.currentTarget)}
                  sx={{ color: t => t.palette.common.white }}
                >
                  <Badge
                    badgeContent={unreadNotificationsAmount}
                    color="error"
                    invisible={!hasUnreadNotifications}
                    max={99}
                  >
                    <NotificationsNoneIcon />
                  </Badge>
                </IconButton>
                <Popover
                  open={notifOpen}
                  anchorEl={notifAnchorEl}
                  onClose={(): void => setNotifAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  slotProps={{
                    paper: {
                      sx: { width: 420, maxHeight: 520, display: 'flex', flexDirection: 'column' },
                    },
                  }}
                >
                  <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="h6" fontWeight={700}>
                        Notifications
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2">Only unread</Typography>
                        <Switch
                          size="small"
                          checked={showUnreadNotifications}
                          onChange={toggleUnreadFilter}
                        />
                      </Box>
                    </Box>
                    {hasUnreadNotifications && (
                      <Button size="small" onClick={markNotificationsAsRead} sx={{ mt: 0.5, p: 0 }}>
                        Mark all as read
                      </Button>
                    )}
                  </Box>
                  <Divider />
                  <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {isLoadingNotificationsHistory && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                      </Box>
                    )}
                    {!isLoadingNotificationsHistory && !!notificationsHistory?.length && (
                      <List disablePadding>
                        {notificationsHistory.map(
                          ({
                            id,
                            sender,
                            action,
                            ticket,
                            sentDate,
                            isRead,
                            actionTypeId,
                            roofTopId: notificationRoofTopId,
                          }) => {
                            const isWarrCloudUser = sender.typeId === UserTypesE.WarrCloud;
                            const senderName = isWarrCloudUser ? 'WarrCloud' : sender.displayName;

                            return (
                              <ListItem
                                key={id}
                                alignItems="flex-start"
                                sx={{
                                  cursor: 'pointer',
                                  bgcolor: isRead ? 'transparent' : 'action.hover',
                                  '&:hover': { bgcolor: 'action.selected' },
                                }}
                                onClick={handleNotificationContentClick({
                                  ticketId: ticket?.id,
                                  notificationId: id,
                                  roNumber: ticket?.roNumber,
                                  actionTypeId,
                                  roofTopId: notificationRoofTopId,
                                })}
                                secondaryAction={
                                  !isRead ? (
                                    <IconButton
                                      edge="end"
                                      size="small"
                                      title="Mark as read"
                                      onClick={(e): void => {
                                        e.stopPropagation();
                                        markNotificationAsRead(id)();
                                      }}
                                    >
                                      <MarkEmailReadOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  ) : undefined
                                }
                              >
                                <ListItemAvatar>
                                  <Avatar
                                    src={isWarrCloudUser ? undefined : sender.photoUrl}
                                    sx={{ width: 32, height: 32, fontSize: 14 }}
                                  >
                                    {getInitials(senderName || '')}
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={
                                    <Typography
                                      variant="body2"
                                      fontWeight={isRead ? 400 : 700}
                                      noWrap
                                      sx={{ pr: 4 }}
                                    >
                                      {senderName} {action}
                                    </Typography>
                                  }
                                  secondary={
                                    <>
                                      <Typography
                                        component="span"
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {format(new Date(sentDate), 'MMM d, yyyy, p')}
                                      </Typography>
                                      {ticket?.roNumber && (
                                        <Typography
                                          component="span"
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                        >
                                          RO: {ticket.roNumber}
                                        </Typography>
                                      )}
                                    </>
                                  }
                                />
                              </ListItem>
                            );
                          }
                        )}
                      </List>
                    )}
                    {!isLoadingNotificationsHistory &&
                      showUnreadNotifications &&
                      !notificationsHistory?.length && (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            You've read all your notifications from the last 30 days
                          </Typography>
                        </Box>
                      )}
                    {!isLoadingNotificationsHistory &&
                      !showUnreadNotifications &&
                      !notificationsHistory?.length && (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            No notifications
                          </Typography>
                        </Box>
                      )}
                  </Box>
                </Popover>
                {toolbarActions}
                <BaseMenu
                  icon={<MenuIcon sx={{ width: 30, height: 30, color: 'common.white' }} />}
                  menuItems={menuItems}
                  boxProps={{}}
                />
              </Box>
            </Box>
          </Toolbar>
        </Box>
        {openedTicketTabs &&
          Object.keys(openedTicketTabs).length > 0 &&
          onTicketTabClick &&
          onCloseTicketTab && (
            <TicketTabs
              tabs={openedTicketTabs}
              tabIndex={ticketTabIndex}
              onTabClick={onTicketTabClick}
              onCloseTab={onCloseTicketTab}
            />
          )}
      </AppBar>
    </Box>
  );
};
